import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { InfoAlert, PadContainer, HorizontalFlex } from 'smartsuite-ui';
import WidgetErrorMessage from '../../../../viewDashboards/widgets/WidgetErrorMessage';
import {
    allMetricSourcesAuthorized,
    hasMetricSources,
    getMetricSourcesForRequests,
    areMetricSourcesTruncated,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../../../viewDashboards/widgets/utils/wrapperUtils';
import ActionedBagsToggle from './ActionedBagsToggle';

// eslint-disable-next-line complexity
function getMetricSource({ metricRequests, metrics }) {
    if (metricRequests.length !== 3) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const metricSources = getMetricSourcesForRequests(metrics, metricRequests);
    if (metricSources.some((metricSource) => !metricSource)) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized(metricSources)) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources(metricSources, false)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        metricSources,
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSources, metricRequests };
}

// eslint-disable-next-line complexity
export default function StationTableWrapper(props) {
    const [showActionedBags, setShowActionedBags] = useState(true);

    const { metricSources, metricRequests, metricSourceErrors, errorKey } =
        getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const headerData = metricSources[0].metricSeries;

    const filteredTableData = showActionedBags
        ? metricSources[1].metricSeries
        : metricSources[1].metricSeries.filter((x) => !x.actioned);

    const headerGroupBy = metricRequests[0].groupBy[0];
    const tableGroupBy = metricRequests[1].groupBy[0];

    return (
        <>
            {areMetricSourcesTruncated(metricSources) && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${metricRequests[0].truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="flight-count-container">
                <HorizontalFlex>
                    {props.isOutbound
                        ? props.customLabels['key:outbound']
                        : props.customLabels['key:inbound']}
                    :
                    <PadContainer cssClassModifiers={['pad-small']} />
                    <span className="flight-tables__count">
                        {metricSources[2].metricSeries[0].count}
                    </span>
                    <PadContainer />
                </HorizontalFlex>
            </div>
            <div className="chart-container">
                <ActionedBagsToggle
                    isActive={showActionedBags}
                    onClick={setShowActionedBags}
                />
                <PadContainer cssClassModifiers={['pad-small']} />
                {headerData.map((flight) => {
                    const tableData = filteredTableData.filter(
                        (x) => x[tableGroupBy] === flight[headerGroupBy]
                    );
                    return (
                        <PadContainer
                            key={flight.flightId}
                            cssClassModifiers={[
                                'no-pad-left-right',
                                'no-pad-top',
                            ]}
                        >
                            <props.StationComponent
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...props}
                                formatter={props.numberDecimalFormatter}
                                headerData={flight}
                                headerRequest={metricRequests[0]}
                                defaultCollapsed={headerData.length > 1}
                                configuration={props.configuration}
                                openLinksInNewTab={props.openLinksInNewTab}
                                showActionedBags={showActionedBags}
                                tableData={tableData}
                                tableRequest={metricRequests[1]}
                            />
                        </PadContainer>
                    );
                })}
            </div>
        </>
    );
}

StationTableWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    isOutbound: PropTypes.bool.isRequired,
    customLabels: PropTypes.instanceOf(Object).isRequired,
};
